import React from "react";
import { Info, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * Parses markdown-style bold tags (**bold text**) without dangerouslySetInnerHTML.
 */
export const Inline = ({ text = "" }) => {
  const str = typeof text === "string" ? text : String(text || "");
  if (!str.includes("**")) return <>{str}</>;

  return (
    <>
      {str.split("**").map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
      )}
    </>
  );
};

/**
 * Renders individual blocks of content (h2, h3, h4, p, ul, ol, callout).
 */
export const TermsBlock = ({ block }) => {
  if (!block) return null;

  switch (block.type) {
    case "h2":
      return (
        <h2 className="terms-h2">
          <Inline text={block.text} />
        </h2>
      );

    case "h3":
      return (
        <h3 className="terms-h3">
          <Inline text={block.text} />
        </h3>
      );

    case "h4":
      return (
        <h4 className="terms-h4">
          <Inline text={block.text} />
        </h4>
      );

    case "ul":
      return (
        <ul className="terms-ul">
          {block.items?.map((item, idx) => (
            <li key={idx}>
              <Inline text={item} />
            </li>
          ))}
        </ul>
      );

    case "ol":
      return (
        <ol className="terms-ol">
          {block.items?.map((item, idx) => (
            <li key={idx}>
              <Inline text={item} />
            </li>
          ))}
        </ol>
      );

    case "callout": {
      const type = block.calloutType || "info";
      return (
        <div className={`terms-callout terms-callout--${type}`} role="note">
          {block.title && (
            <div className="terms-callout-title">
              {type === "warning" ? (
                <AlertTriangle size={17} className="text-warning flex-shrink-0" />
              ) : type === "success" ? (
                <CheckCircle2 size={17} className="text-success flex-shrink-0" />
              ) : (
                <Info size={17} className="text-primary flex-shrink-0" />
              )}
              <span>{block.title}</span>
            </div>
          )}
          <p className="terms-callout-text">
            <Inline text={block.text} />
          </p>
        </div>
      );
    }

    case "p":
    default:
      return (
        <p className="terms-p">
          <Inline text={block.text} />
        </p>
      );
  }
};

export default function TermsBlockRenderer({ blocks = [] }) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return <p className="text-muted">No clauses available for this section.</p>;
  }

  return (
    <div className="terms-blocks-container">
      {blocks.map((block, idx) => (
        <TermsBlock key={idx} block={block} />
      ))}
    </div>
  );
}

